import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n";
import { Send, ArrowRight } from "lucide-react";

function generateReference() {
  const now = new Date();
  const yymm = `${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, "0")}`;
  const rand = Math.floor(Math.random() * 90000 + 10000);
  return `ZR-${yymm}-${rand}`;
}

export default function ContactForm() {
  const t = useT();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = t("form.errors.nameRequired");
    if (!values.email.trim()) errs.email = t("form.errors.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errs.email = t("form.errors.emailInvalid");
    if (!values.message.trim()) errs.message = t("form.errors.messageRequired");
    else if (values.message.trim().length < 8) errs.message = t("form.errors.messageShort");
    return errs;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    const reference = generateReference();
    try {
      sessionStorage.setItem(
        "zr_last_submission",
        JSON.stringify({ ...values, reference, submittedAt: new Date().toISOString() }),
      );
    } catch (_) { /* noop */ }
    // Simulate small delay so the user sees the state
    setTimeout(() => {
      navigate("/thank_you");
    }, 350);
  };

  return (
    <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="space-y-5">
      <div>
        <label htmlFor="cf-name" className="block text-[11px] uppercase tracking-[0.22em] text-[#8f96a3] mb-2">
          {t("form.name")}
        </label>
        <input
          id="cf-name"
          type="text"
          value={values.name}
          onChange={setField("name")}
          placeholder={t("form.namePlaceholder")}
          data-testid="cf-name"
          className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#C9F14B] outline-none py-3 text-base text-[#F1ECDE] placeholder-[#6b727d] transition"
        />
        {errors.name && <p className="mt-2 text-xs text-[#FF8B6B]">{errors.name}</p>}
      </div>
      <div>
        <label htmlFor="cf-email" className="block text-[11px] uppercase tracking-[0.22em] text-[#8f96a3] mb-2">
          {t("form.email")}
        </label>
        <input
          id="cf-email"
          type="email"
          value={values.email}
          onChange={setField("email")}
          placeholder={t("form.emailPlaceholder")}
          data-testid="cf-email"
          className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#C9F14B] outline-none py-3 text-base text-[#F1ECDE] placeholder-[#6b727d] transition"
        />
        {errors.email && <p className="mt-2 text-xs text-[#FF8B6B]">{errors.email}</p>}
      </div>
      <div>
        <label htmlFor="cf-message" className="block text-[11px] uppercase tracking-[0.22em] text-[#8f96a3] mb-2">
          {t("form.message")}
        </label>
        <textarea
          id="cf-message"
          rows={4}
          value={values.message}
          onChange={setField("message")}
          placeholder={t("form.messagePlaceholder")}
          data-testid="cf-message"
          className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#C9F14B] outline-none py-3 text-base text-[#F1ECDE] placeholder-[#6b727d] transition resize-none"
        />
        {errors.message && <p className="mt-2 text-xs text-[#FF8B6B]">{errors.message}</p>}
      </div>
      <p className="text-xs text-[#8f96a3] leading-relaxed">{t("form.consent")}</p>
      <div className="pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="cf-submit"
          className="group inline-flex items-center gap-2 rounded-full bg-[#C9F14B] text-[#0B0F14] px-6 py-3 text-sm font-medium hover:bg-[#d7ff5f] transition-colors disabled:opacity-70"
        >
          {submitting ? t("form.sending") : t("form.submit")}
          {submitting ? <Send size={16} /> : <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />}
        </button>
      </div>
    </form>
  );
}
